import { QueryClient, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import head from "lodash-es/head"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isNumber from "lodash-es/isNumber"
import last from "lodash-es/last"
import { usePrevious, useUnmount, useUpdateEffect } from "react-use"

import { generateUuid, reduxBatch } from "@/3514/utils"

import {
    type TCoachingChatConversationQuery as TConversationQuery,
    type TCoachingChatNewMessagesQuery as TNewMessagesQuery,
    coachingChatQueries as queries
} from "../api"
import { ECoachingChatQueryKey as EQueryKey, coachingChatApiTimers as apiTimers } from "../config"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationErrorSelector as useConversationErrorSelector,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationLastMessageSelector as useConversationLastMessageSelector,
    useCoachingChatConversationMessagesPresenceSelector as useConversationMessagesPresenceSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatMessageAuthor as EConversationMessageAuthor,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatMessageTransport as EMessageTransport,
    type TCoachingChatConversation as TConversation,
    type TCoachingChatConversationError as TConversationError,
    type TCoachingChatConversationMessage as TConversationMessage,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const { useConversationHooks } = queries
const {
    NEW_MESSAGES_QUERY_LONG_POLLING_REFETCH_INTERVAL_IN_MS: LONG_POLLING_INTERVAL,
    NEW_MESSAGES_QUERY_SHORT_POLLING_REFETCH_INTERVAL_IN_MS: SHORT_POLLING_INTERVAL
} = apiTimers

const {
    queriesModelsDataExtractors: {
        getConversationErrorVariantByAxiosError: getChatErrorVariantFromAxiosError,
        adaptConversationToStore: conversationQueryModelAdapter,
        getNewMessagesMessages: getChatMessagesFromNewMessagesQueryModel,
        getNewMessagesSummaries: getChatSummaryFromNewMessagesQueryModel,
        getNewMessagesErrors: getChatErrorsFromNewMessagesQueryModel
    }
} = uiUtils

type _TParams = {
    entityId: number
    entityMode: EEntityMode
    wasChatCompleted: boolean
    existingConversationId: number | undefined
}

function useCoachingChatRestQueriesManager({
    entityId,
    entityMode,
    wasChatCompleted,
    existingConversationId
}: _TParams): void {
    const queryClient: QueryClient = useQueryClient()

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationError: TConversationError = useConversationErrorSelector(entityId)

    const areMessagesPresent: boolean = useConversationMessagesPresenceSelector(entityId)

    const lastMessage: TConversationMessage = useConversationLastMessageSelector(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const prevConversationApiMode: EConversationApiMode = usePrevious<EConversationApiMode>(conversationApiMode)

    const isRestApiMode: boolean = eq(conversationApiMode, EConversationApiMode.Rest)
    const isPrevApiModeSocket: boolean = eq(prevConversationApiMode, EConversationApiMode.Sockets)

    const isLightMode: boolean = eq(entityMode, EEntityMode.Light)

    const [
        { data: conversationQueryData, error: conversationQueryError },
        { data: newMessagesQueryData, error: newMessagesQueryError }
    ]: [TConversationQuery, TNewMessagesQuery] = useConversationHooks({
        conversationId,
        conversationQueryParams: {
            enabled:
                Boolean(
                    wasChatCompleted ? isNumber(conversationId) : isNumber(existingConversationId) && isRestApiMode
                ) && Boolean(isNumber(conversationId))
        },
        newMessagesQueryParams: {
            enabled:
                isLightMode || !isRestApiMode || wasChatCompleted
                    ? false
                    : isRestApiMode &&
                      isNumber(conversationId) &&
                      isNull(conversationError.variant) &&
                      includes<EConversationStatus>(
                          [EConversationStatus.Ready, EConversationStatus.InProgress],
                          conversationStatus
                      ),
            refetchInterval: !areMessagesPresent
                ? SHORT_POLLING_INTERVAL
                : (!lastMessage?.isStreamable && eq(lastMessage?.status, EConversationMessagePhase.Waiting)) ||
                    (isRestApiMode && isPrevApiModeSocket)
                  ? SHORT_POLLING_INTERVAL
                  : LONG_POLLING_INTERVAL
        }
    })

    useUpdateEffect((): void => {
        if (!isNull(conversationQueryError)) {
            const _err: EConversationErrorVariant | undefined = getChatErrorVariantFromAxiosError(
                conversationQueryError,
                { isChatForceDisabled: true }
            )

            reduxBatch((): void => {
                conversationDispatcher.triggerError(_err)

                if (eq(_err, EConversationErrorVariant.ApiFailure))
                    conversationDispatcher.changeStatus(EConversationStatus.Paused)
                conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
            })
        }
    }, [conversationQueryError])

    useUpdateEffect((): void => {
        if (!isEmpty(conversationQueryData)) {
            const _conversation: TConversation = conversationQueryModelAdapter(conversationQueryData)
            const _messages: TConversationMessage[] = _conversation.messages
            const _isEmptyMessages: boolean = isEmpty(_messages)
            const _isLastAi: boolean = _isEmptyMessages
                ? false
                : eq(last<TConversationMessage>(_messages).author, EConversationMessageAuthor.AI)

            reduxBatch((): void => {
                conversationDispatcher.loadConversation(_conversation, null)

                if (!_isLastAi || _isEmptyMessages) {
                    conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                    conversationDispatcher.addMessage({
                        id: 1,
                        transport: EMessageTransport.Rest,
                        author: EConversationMessageAuthor.AI,
                        content: String(""),
                        isStreamable: false,
                        status: EConversationMessagePhase.Waiting,
                        clientMessageId: generateUuid(),
                        conversationId: _conversation.id
                    })
                }

                if (_isLastAi) conversationDispatcher.markInput({ isDimmed: false, isDisabled: false })
            })
        }
    }, [conversationQueryData])

    useUpdateEffect((): void => {
        if (!isEmpty(newMessagesQueryData)) {
            const errors: TConversationError[] = getChatErrorsFromNewMessagesQueryModel(newMessagesQueryData)
            const messages: TConversationMessage[] = getChatMessagesFromNewMessagesQueryModel(newMessagesQueryData)
            const summary: TSummaryCard[] = getChatSummaryFromNewMessagesQueryModel(newMessagesQueryData)

            reduxBatch((): void => {
                if (!isEmpty(errors)) {
                    const { variant: fVariant, payload: fPayload }: TConversationError =
                        head<TConversationError>(errors)

                    conversationDispatcher.triggerError(fVariant, fPayload)
                    conversationDispatcher.changeStatus(EConversationStatus.Paused)

                    if (eq(fVariant, EConversationErrorVariant.Violation)) conversationDispatcher.removeMessagesRight(2)
                    else if (
                        includes<EConversationErrorVariant>(
                            [EConversationErrorVariant.AiStuck, EConversationErrorVariant.ApiFailure],
                            fVariant
                        )
                    )
                        conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                }

                if (!isEmpty(messages)) {
                    const { id: fId, content: fContent }: TConversationMessage = head<TConversationMessage>(messages)

                    conversationDispatcher.updateLastMessage({
                        id: fId,
                        content: fContent,
                        status: EConversationMessagePhase.Done
                    })

                    conversationDispatcher.markInput({ isDimmed: false, isDisabled: false })

                    if (!!messages.length) conversationDispatcher.populateMessages(messages)
                }

                if (!isEmpty(summary)) {
                    conversationDispatcher.populateSummary(summary)
                    conversationDispatcher.changeStatus(EConversationStatus.GeneratingSummary)
                } else conversationDispatcher.changeStatus(EConversationStatus.InProgress)
            })
        }
    }, [newMessagesQueryData])

    useUpdateEffect((): void => {
        if (!isNull(newMessagesQueryError)) {
            const _errVariant: EConversationErrorVariant | undefined =
                getChatErrorVariantFromAxiosError(newMessagesQueryError)

            reduxBatch((): void => {
                conversationDispatcher.triggerError(_errVariant)

                conversationDispatcher.changeStatus(EConversationStatus.Paused)

                if (eq(_errVariant, EConversationErrorVariant.ApiFailure))
                    conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
            })
        }
    }, [newMessagesQueryError])

    useUnmount((): void => {
        setConversationApiMode(null)

        if (
            isRestApiMode &&
            eq(conversationQueryData?.status, "in_progress") &&
            includes<EConversationStatus>(
                [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
                conversationStatus
            )
        )
            void queryClient.refetchQueries({
                queryKey: EQueryKey.Conversation(conversationId)
            })
    })
}

export { useCoachingChatRestQueriesManager }
